import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  DirectionsRun as DirectionsRunIcon,
  BarChart as BarChartIcon,
  FitnessCenter as FitnessCenterIcon,
  People as PeopleIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../../store';
import { logoutUser } from '../../store/slices/authSlice';
import { toggleTheme } from '../../store/slices/uiSlice';
import type { AppDispatch } from '../../store';

interface HeaderProps {
  onMenuClick?: () => void;
}

/**
 * Header Component
 * 
 * Main application header with navigation, user controls, and theme toggle.
 * Provides consistent navigation across all authenticated pages with full theme support.
 * 
 * Features:
 * - Responsive navigation with mobile menu toggle
 * - Theme toggle button (sun/moon icon) for light/dark mode switching
 * - Notifications badge with dropdown
 * - User profile menu with role-based navigation items
 * - Logout functionality
 * - Role-based navigation items display
 * - Theme-aware styling using CSS variables and MUI theme
 * - Full dark mode support
 * 
 * Theme Integration:
 * - Reads current theme from Redux store
 * - Dispatches toggleTheme action to switch between light and dark modes
 * - Displays appropriate icon (sun for light mode, moon for dark mode)
 * - Persists theme preference to localStorage via Redux action
 * - Uses theme-aware colors for all UI elements
 */
const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { user, role } = useSelector((state: RootState) => state.auth);
  const { notifications } = useSelector((state: RootState) => state.ui);
  const currentTheme = useSelector((state: RootState) => state.ui.theme || 'light');

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState<null | HTMLElement>(null);

  const unreadNotificationsCount = notifications?.filter(n => !n.read).length || 0;

  // Get user's full name
  const getUserFullName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user?.first_name) {
      return user.first_name;
    }
    if (user?.email) {
      return user.email;
    }
    return 'UserProfile';
  };

  // Get user's initials for avatar fallback
  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    if (user?.first_name) {
      return user.first_name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenNotifications = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNotifications(event.currentTarget);
  };

  const handleCloseNotifications = () => {
    setAnchorElNotifications(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleCloseUserMenu();
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    handleCloseUserMenu();
    navigate('/login');
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  const getNavigationItems = () => {
    const items = [];

    if (role === 'athlete' || role === 'both') {
      items.push(
        { label: 'Dashboard', icon: <DashboardIcon />, path: '/athlete/dashboard' },
        { label: 'Activities', icon: <DirectionsRunIcon />, path: '/athlete/activities' },
        { label: 'Analytics', icon: <BarChartIcon />, path: '/athlete/analytics' },
        { label: 'Workouts', icon: <FitnessCenterIcon />, path: '/athlete/workouts' }
      );
    }

    if (role === 'trainer' || role === 'both') {
      items.push(
        { label: 'Athletes', icon: <PeopleIcon />, path: '/trainer/athletes' },
        { label: 'Templates', icon: <FitnessCenterIcon />, path: '/trainer/templates' }
      );
    }

    if (role === 'superuser') {
      items.push(
        { label: 'Admin Dashboard', icon: <AdminPanelSettingsIcon />, path: '/admin/dashboard' },
        { label: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
        { label: 'System Health', icon: <BarChartIcon />, path: '/admin/health' }
      );
    }

    return items;
  };

  const navigationItems = getNavigationItems();
  const userFullName = getUserFullName();
  const userInitials = getUserInitials();

  return (
    <AppBar
      position="sticky"
      elevation={1}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        borderRadius: 0,
      }}
    >
      <Toolbar>
        {onMenuClick && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuClick}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 0,
            mr: 4,
            cursor: 'pointer',
            fontWeight: 600,
          }}
          onClick={() => navigate('/')}
        >
          Surge
        </Typography>

        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 2 }}>
          {navigationItems.slice(0, 4).map((item) => (
            <Box
              key={item.path}
              onClick={() => navigate(item.path)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              {item.icon}
              <Typography variant="body2">{item.label}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={`Switch to ${currentTheme === 'light' ? 'dark' : 'light'} mode`}>
            <IconButton
              color="inherit"
              onClick={handleThemeToggle}
              aria-label="toggle theme"
              sx={{
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'rotate(180deg)',
                },
              }}
            >
              {currentTheme === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
            </IconButton>
          </Tooltip>

          <IconButton
            color="inherit"
            onClick={handleOpenNotifications}
            aria-label="notifications"
          >
            <Badge badgeContent={unreadNotificationsCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <IconButton
            onClick={handleOpenUserMenu}
            sx={{ p: 0, ml: 1 }}
            aria-label="user menu"
          >
            <Avatar
              alt={userFullName}
              src={user?.profile_picture_url || undefined}
              sx={{ width: 36, height: 36 }}
            >
              {userInitials}
            </Avatar>
          </IconButton>
        </Box>

        <Menu
          anchorEl={anchorElNotifications}
          open={Boolean(anchorElNotifications)}
          onClose={handleCloseNotifications}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: { width: 320, maxHeight: 400 },
          }}
        >
          <MenuItem onClick={() => handleNavigate('/notifications')}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              View All Notifications
            </Typography>
          </MenuItem>
          <Divider />
          {notifications && notifications.length > 0 ? (
            notifications.slice(0, 5).map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={handleCloseNotifications}
                sx={{
                  backgroundColor: notification.read ? 'transparent' : 'action.hover',
                  whiteSpace: 'normal',
                  py: 1.5,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: notification.read ? 400 : 600 }}>
                    {notification.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {notification.message}
                  </Typography>
                </Box>
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </MenuItem>
          )}
        </Menu>

        <Menu
          anchorEl={anchorElUser}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: { width: 240 },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {userFullName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
            <Typography variant="caption" display="block" color="primary" sx={{ mt: 0.5 }}>
              {role === 'both'
                ? 'Athlete & Trainer'
                : role
                  ? role.charAt(0).toUpperCase() + role.slice(1)
                  : 'Unknown Role'
              }
            </Typography>
          </Box>
          <Divider />

          {navigationItems.length > 4 && (
            <>
              {navigationItems.slice(4).map((item) => (
                <MenuItem key={item.path} onClick={() => handleNavigate(item.path)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              ))}
              <Divider />
            </>
          )}

          <MenuItem onClick={() => handleNavigate('/settings')}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;